<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <div>
                    <p class="item-name">
                        Summary
                    </p>
                    <QTable
                        class="sticky-table"
                        :data="summaryData"
                        :columns="summaryColumns"
                        :hide-bottom="true"
                        :rows-per-page-options="[0]"
                        row-key="id"
                        :loading="isLoading"
                        virtual-scroll
                    >
                        <template v-slot:header="props">
                            <q-tr :props="props">
                                <q-th
                                    v-for="col in props.cols"
                                    :key="col.name"
                                    :props="props"
                                    class="sticky-header"
                                >
                                    <div
                                        v-if="col.label == 'Diagnosis'"
                                        class="d-flex align-items-center"
                                    >
                                        <div
                                            @click="toggleExpandAll()"
                                            class="pr-2"
                                        >
                                            <i
                                                :class="
                                                    checkExpandIcon(isExpandAll)
                                                "
                                                class="cursor-pointer m-auto"
                                            ></i>
                                        </div>
                                        <div>
                                            <strong
                                                @click="toggleExpandAll()"
                                                class="cursor-pointer"
                                            >
                                                {{ col.label }}
                                            </strong>
                                        </div>
                                    </div>
                                    <strong v-else>{{ col.label }}</strong>
                                </q-th>
                            </q-tr>
                            <q-linear-progress
                                v-show="isLoading"
                                class="sticky-loader"
                                indeterminate
                                color="dark"
                                :style="isHasExpand ? 'top:96px' : 'top:48px'"
                            />
                        </template>
                        <template v-slot:body="props">
                            <q-tr :props="props" :key="`m_${props.key}`">
                                <q-td
                                    v-for="col in props.cols"
                                    :key="col.name"
                                    :props="props"
                                >
                                    <div
                                        v-if="col.label == 'Diagnosis'"
                                        class="d-flex align-items-center"
                                    >
                                        <span
                                            @click="toggleExpandItem(props)"
                                            class="pr-2"
                                        >
                                            <i
                                                class="cursor-pointer"
                                                :class="
                                                    checkExpandIcon(
                                                        props.row.expand
                                                    )
                                                "
                                            ></i>
                                        </span>
                                        <div
                                            @click="toggleExpandItem(props)"
                                            class="text-link"
                                        >
                                            {{ col.value }}
                                        </div>
                                    </div>
                                    <div v-else>
                                        {{ col.value }}
                                    </div>
                                </q-td>
                            </q-tr>
                            <q-tr
                                v-if="props.row.expand"
                                :props="props"
                                class="color-grey q-virtual-scroll--with-prev"
                                :key="`e_${props.key}`"
                                no-hover
                            >
                                <q-td
                                    colspan="100%"
                                    class="border-left py-4 pl-0 pr-1 color-grey"
                                >
                                    <div class="table-responsive mb-0">
                                        <table
                                            class="table table-hover table-inner"
                                        >
                                            <thead>
                                                <tr class="table-borderless">
                                                    <th
                                                        class="column-close table-inner-header-close"
                                                    ></th>
                                                    <th
                                                        v-for="(col,
                                                        i) in diagnosisColumn"
                                                        :key="i"
                                                        :style="col.style"
                                                    >
                                                        {{ col.label }}:
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr
                                                    v-if="
                                                        diagnosisDataLength ===
                                                        0
                                                    "
                                                >
                                                    <td colspan="8">
                                                        <i
                                                            >No records
                                                            available</i
                                                        >
                                                    </td>
                                                </tr>
                                                <tr
                                                    v-else
                                                    v-for="(patient,
                                                    i) in diagnosisData[
                                                        props.row.id
                                                    ]"
                                                    :key="i"
                                                >
                                                    <td class="border-0"></td>
                                                    <td>
                                                        <patient-name
                                                            :use-router="
                                                                USE_PATIENT_MODULE_VUE
                                                            "
                                                            :patient="
                                                                generatePatientObject(
                                                                    patient
                                                                )
                                                            "
                                                            :showStatus="true"
                                                        />
                                                    </td>
                                                    <td>
                                                        {{ patient.species }}
                                                    </td>
                                                    <td>
                                                        {{ patient.sex }}
                                                    </td>
                                                    <td>
                                                        {{
                                                            patient.status_name
                                                        }}
                                                    </td>
                                                    <td>
                                                        {{ patient.acuity }}
                                                    </td>
                                                    <td>
                                                        {{
                                                            _setDateTimeDisplay(
                                                                patient.date_opened
                                                            )
                                                        }}
                                                    </td>
                                                    <td>
                                                        {{
                                                            _setDateTimeDisplay(
                                                                patient.date_closed
                                                            )
                                                        }}
                                                    </td>
                                                    <td>
                                                        {{ patient.note }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </q-td>
                            </q-tr>
                        </template>
                        <template
                            v-slot:bottom-row
                            v-if="
                                summaryDataLength == 0 && !isLoading && !isError
                            "
                        >
                            <q-tr>
                                <q-td colspan="12">No records available</q-td>
                            </q-tr>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
    </div>
</template>
<script>
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import { some } from "lodash";
import { QTable } from "quasar";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import PatientName from "@/components/commons/patient-name";
import { map } from "lodash";

const ICON_COLLAPSE = "fas fa-chevron-circle-right";
const ICON_EXPAND = "fas fa-chevron-circle-down";
export default {
    components: {
        QTable,
        PatientName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object
        }
    },
    data() {
        return {
            RECORD_PER_PAGE: 50,
            isDestroyed: false,
            isLoading: true,
            summaryData: [],
            diagnosisData: [],
            summaryDataLength: 0,
            diagnosisDataLength: 0,
            title: "Diagnosis Summary",
            isError: false,
            params: {
                option: "0",
                values: [],
                status: "0",
                species: []
            },
            isExpandAll: false
        };
    },
    async mounted() {
        let self = this;
        await self._getSetting();
        self.data.reportId = CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST;
        self.data.isShowDateRange = false;
        self.data.species = [];
        self.data.diagnosis = [];
        self.data.diagnosisStatus = 0;

        self.init();
        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
    },

    beforeDestroy: function () {
        this.isDestroyed = true;
        this.data.reportId = null;
        this.data.isShowDateRange = true;
        this.data.species = [];
        this.data.diagnosis = [];
        this.data.diagnosisStatus = 0;
        this.resetPagination();
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
    },
    methods: {
        prepareData(data) {
            this.isLoading = true;
            if (data) {
                this.data.species = data.species;
                this.data.diagnosis = data.diagnosis;

                let hasDiagnosis = data.diagnosis && data.diagnosis.length > 0;
                this.params.option = hasDiagnosis ? "1" : "0";
                this.params.values = hasDiagnosis
                    ? map(data.diagnosis, "value")
                    : [];
                this.params.status = data.diagnosisStatus;
                this.params.species =
                    data.species.length > 0 ? map(data.species, "value") : [];
            }
            this.resetPagination();
            this.init();
        },
        async init() {
            let self = this;
            self.toggleOptions(false);
            self.isDestroyed = false;
            self.isLoading = true;
            self.isError = false;
            let params = {
                option: self.params.option,
                values: self.params.values,
                status: self.params.status,
                species: self.params.species,
                page: self.pagination.page,
                perPage: self.RECORD_PER_PAGE
            };

            clearInterval(self.pagination.interval);
            await self
                .fetchRecords(params)
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.summaryData = res.resource.results.summary.map(
                            v => ({
                                ...v,
                                expand: false
                            })
                        );
                        self.summaryDataLength = self.summaryData.length;
                        self.diagnosisData = self.prepareDiagnosisData(
                            res.resource.results.diagnoses
                        );
                        self.diagnosisDataLength = self.diagnosisData.length;
                        self.pagination.lastPage = Math.ceil(
                            res.resource.meta.item_count / self.RECORD_PER_PAGE
                        );
                        self.continueFetch(params);
                        self.dynamicPageTitle(self.title);
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self.isError = true;
                    self.isLoading = false;
                    self.loadingCompleted();
                    self._message("Error: Unable to get the report", "danger");
                });
        },
        async continueFetch(params) {
            let self = this;

            while (
                self.pagination.page < self.pagination.lastPage &&
                !self.isDestroyed &&
                !self.isError
            ) {
                self.pagination.page += 1;
                params.page = self.pagination.page;
                await self
                    .fetchRecords(params)
                    .then(res => {
                        if (res.message.code === 200) {
                            self.summaryData = self.summaryData.concat(
                                res.resource.results.summary.map(v => ({
                                    ...v,
                                    expand: false
                                }))
                            );
                            self.summaryDataLength = self.summaryData.length;
                            self.diagnosisData = {
                                ...self.diagnosisData,
                                ...self.prepareDiagnosisData(
                                    res.resource.results.diagnoses
                                )
                            };
                            self.diagnosisDataLength =
                                self.diagnosisData.length;
                        } else {
                            throw new Error(
                                "Error: Unable to continue get the report"
                            );
                        }
                    })
                    .catch(e => {
                        console.error(e);
                        self.isError = true;
                        self.isLoading = false;
                        self.summaryData = [];
                        self.summaryDataLength = 0;
                        self.diagnosisData = [];
                        self.diagnosisDataLength = 0;
                        self.loadingCompleted();
                        self._message(
                            "Error: Unable to continue get the report",
                            "danger"
                        );
                    });
            }
            self.loadingCompleted();
            setTimeout(() => {
                self.toggleOptions(true);
                self.isLoading = false;
            }, 1000);
        },
        fetchRecords(params) {
            this.loadingPercentages();
            return ReportService.getReport(
                CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST,
                params
            );
        },
        prepareDiagnosisData(diagnosisData) {
            let newDiagnosisData = {};
            Object.keys(diagnosisData).forEach(key => {
                if (diagnosisData[key].length > 0) {
                    let id = diagnosisData[key][0].id;
                    newDiagnosisData[id] = diagnosisData[key];
                }
            });
            return newDiagnosisData;
        },
        toggleExpandItem(props) {
            props.row.expand = !props.row.expand;

            // check if all data expand same value, change this.isExpandAll to be same value
            if (!some(this.summaryData, { expand: !props.row.expand })) {
                this.isExpandAll = props.row.expand;
            }
        },
        checkExpandIcon(isExpand) {
            return isExpand ? ICON_EXPAND : ICON_COLLAPSE;
        },
        toggleExpandAll() {
            let self = this;
            self.isExpandAll = !self.isExpandAll;
            self.summaryData = self.summaryData.map(result => {
                result.expand = self.isExpandAll;
                return result;
            });
        },
        toggleOptions(value) {
            // to hide the action menu when data is loading
            this.data.exportOption = value;
            this.data.refreshOption = value;
            this.data.filterDateOption = value;
        },
        generatePatientObject(row) {
            return {
                id: row.patient_id,
                name: row.patient,
                encoded_id: row.patient_encoded_id,
                isactive: true
            };
        },
        generateIdTable(diagnosis) {
            return diagnosis
                .toLowerCase()
                .replaceAll("_", "")
                .replaceAll(/\W+/g, "");
        }
    },
    computed: {
        summaryColumns() {
            return [
                {
                    name: "diagnosis",
                    label: "Diagnosis",
                    field: "diagnosis",
                    align: "left",
                    classes: "text-capitalize",
                    style: "white-space: nowrap"
                },
                {
                    name: "open",
                    label: "Open",
                    field: "opened",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "closed",
                    label: "Closed",
                    field: "closed",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "total",
                    label: "Total",
                    field: "total",
                    style: "white-space: nowrap",
                    align: "left"
                }
            ];
        },
        diagnosisColumn() {
            return [
                {
                    label: "Patient",
                    style: "min-width:200px"
                },
                {
                    label: "Species (Breed)",
                    style: "min-width:200px"
                },
                {
                    label: "Sex",
                    style: "min-width:200px"
                },
                {
                    label: "Status",
                    style: "white-space: nowrap;min-width:100px"
                },
                {
                    label: "Acuity",
                    style: "white-space: nowrap;min-width:80px"
                },
                {
                    label: "Opened",
                    style: "white-space: nowrap;min-width:110px"
                },
                {
                    label: "Closed",
                    style: "white-space: nowrap;min-width:110px"
                },
                {
                    label: "Notes",
                    style: "min-width:200px"
                }
            ];
        }
    }
};
</script>
